/**
 * 运营 - 客户转移（REQ-003 F002 前端，原「商户转移管理」更名）
 *
 * 列表 = customer_sales_relation 全量记录（每行 = merchant+dis_company 一条记录，不按商户聚合）
 * 6 列：商户名称 / 下发公司 / 电销 / 面销 / 转交电销 / 转交面销 + 操作列（转交按钮）
 * 转交弹窗：选择新电销/新面销（在职销售下拉，可只填其一或同人），至少转一个
 *
 * REQ-003 F003 增强：转交弹窗布局对齐（label-position=left + 提示区/下拉/按钮左右对齐）
 * REQ-003 F04 增强：搜索区四字段下拉（电销/面销/转交电销/转交面销，数据源=全部在职销售 getSalesList，
 *                   :value 绑定销售 ID，字面字段 ID 等值查询，与商户名/下发公司叠加 AND）
 * REQ-003 F005 增强：批量转交（行勾选 + 表头全选当前页 + 「批量转交」按钮 + 批量弹窗：
 *                   勾选模式走 records / 「全选当前搜索结果(跨页)」开关走 searchParams）
 *
 * 接口契约：.ai-dev/contracts/v1/sales-transfers.yml（v1.0.3，T-001/T-007/T-008 后端已实现）
 */
<template>
  <div class="merchant-transfer-page">
    <el-breadcrumb separator-class="el-icon-arrow-right" class="page-breadcrumb">
      <el-breadcrumb-item :to="{ path: '/oms/customerSignOrder' }">运营</el-breadcrumb-item>
      <el-breadcrumb-item>客户转移</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 筛选区（REQ-003 F04: 新增四字段下拉，与商户名/下发公司并存，叠加 AND） -->
    <div class="search-section">
      <el-form :inline="true" :model="formInline" size="small" class="search-form">
        <el-form-item label="商户名称">
          <el-input
            v-model="formInline.merchantName"
            placeholder="请输入商户名称"
            clearable
            class="search-input"
            @keyup.enter.native="search"
          ></el-input>
        </el-form-item>
        <el-form-item label="下发公司">
          <el-input
            v-model="formInline.disCompanyName"
            placeholder="请输入下发公司名称"
            clearable
            class="search-input"
            @keyup.enter.native="search"
          ></el-input>
        </el-form-item>
        <el-form-item label="电销">
          <el-select
            v-model="formInline.phoneSalesId"
            placeholder="请选择电销"
            clearable
            filterable
            class="search-select"
          >
            <el-option
              v-for="item in searchSalesOptions"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="面销">
          <el-select
            v-model="formInline.faceSalesId"
            placeholder="请选择面销"
            clearable
            filterable
            class="search-select"
          >
            <el-option
              v-for="item in searchSalesOptions"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="转交电销">
          <el-select
            v-model="formInline.transferPhoneSalesId"
            placeholder="请选择转交电销"
            clearable
            filterable
            class="search-select"
          >
            <el-option
              v-for="item in searchSalesOptions"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="转交面销">
          <el-select
            v-model="formInline.transferFaceSalesId"
            placeholder="请选择转交面销"
            clearable
            filterable
            class="search-select"
          >
            <el-option
              v-for="item in searchSalesOptions"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="reset">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 列表 -->
    <el-card shadow="hover" class="table-card">
      <!-- 批量操作区（REQ-003 F005: 勾选 ≥1 行后「批量转交」可点击） -->
      <div class="batch-toolbar">
        <el-button
          type="primary"
          size="small"
          icon="el-icon-sort"
          :disabled="selectedRows.length === 0"
          @click="openBatchDialog"
        >批量转交</el-button>
        <span class="batch-selected-tip">
          已选 <span class="tip-strong">{{ selectedRows.length }}</span> 条记录
        </span>
      </div>
      <el-table
        ref="tableRef"
        size="small"
        :data="listData"
        v-loading="loading"
        element-loading-text="加载中..."
        empty-text="暂无数据"
        style="width: 100%"
        border
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="48" align="center"></el-table-column>
        <el-table-column prop="merchantName" label="商户名称" min-width="160" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.merchantName || '--' }}
          </template>
        </el-table-column>
        <el-table-column prop="disCompanyName" label="下发公司" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.disCompanyName || '--' }}
          </template>
        </el-table-column>
        <el-table-column prop="phoneSalesName" label="电销" min-width="100" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.phoneSalesName || '' }}
          </template>
        </el-table-column>
        <el-table-column prop="faceSalesName" label="面销" min-width="100" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.faceSalesName || '' }}
          </template>
        </el-table-column>
        <el-table-column prop="transferPhoneSalesName" label="转交电销" min-width="110" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.transferPhoneSalesName || '--' }}
          </template>
        </el-table-column>
        <el-table-column prop="transferFaceSalesName" label="转交面销" min-width="110" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.transferFaceSalesName || '--' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button type="primary" size="mini" plain @click="openTransferDialog(scope.row)">转交</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrap">
        <Pagination v-bind:child-msg="pageparm" @callFather="callFather" />
      </div>
    </el-card>

    <!-- 转交弹窗（REQ-003 F003: label-position=left + 提示区/表单/按钮对齐） -->
    <el-dialog
      title="转交"
      :visible.sync="transferDialogVisible"
      width="520px"
      class="transfer-dialog"
      @close="resetTransferForm"
    >
      <div class="transfer-tip">
        <p>商户：<span class="tip-strong">{{ currentRow.merchantName || '--' }}</span></p>
        <p>下发公司：<span class="tip-strong">{{ currentRow.disCompanyName || '--' }}</span></p>
        <p class="tip-small">转交只更新转交电销/转交面销，原电销/面销归属保持不变；可只转其一，也可同时转给同一人。</p>
      </div>
      <el-form
        ref="transferFormRef"
        :model="transferForm"
        :rules="transferRules"
        label-width="100px"
        label-position="left"
      >
        <el-form-item label="新电销：" prop="newPhoneSales">
          <el-select
            v-model="transferForm.newPhoneSales"
            placeholder="请选择新电销（可选）"
            filterable
            clearable
            style="width: 100%"
          >
            <el-option
              v-for="sale in salesOptions"
              :key="sale.id"
              :label="sale.name"
              :value="sale.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="新面销：" prop="newFaceSales">
          <el-select
            v-model="transferForm.newFaceSales"
            placeholder="请选择新面销（可选）"
            filterable
            clearable
            style="width: 100%"
          >
            <el-option
              v-for="sale in salesOptions"
              :key="sale.id"
              :label="sale.name"
              :value="sale.id"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="transferDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="submitLoading" @click="submitTransfer">确定</el-button>
      </span>
    </el-dialog>

    <!-- 批量转交弹窗（REQ-003 F005: 勾选模式 / 跨页全选模式二选一提交） -->
    <el-dialog
      title="批量转交"
      :visible.sync="batchDialogVisible"
      width="520px"
      class="transfer-dialog"
      @close="resetBatchForm"
    >
      <div class="transfer-tip">
        <p class="tip-strong">{{ crossPage ? '将转交当前搜索结果全部记录，共 ' + (pageparm.total || 0) + ' 条' : '已选 ' + selectedRows.length + ' 条记录' }}</p>
        <p class="tip-small">批量转交只更新转交电销/转交面销，原电销/面销归属保持不变；提交后不可撤销，可重复转交（后一次覆盖前一次）。</p>
      </div>
      <el-checkbox v-model="crossPage" class="batch-cross-checkbox">全选当前搜索结果（跨页）</el-checkbox>
      <el-form
        ref="batchFormRef"
        :model="batchForm"
        label-width="100px"
        label-position="left"
        class="batch-form"
      >
        <el-form-item label="新电销">
          <el-select
            v-model="batchForm.newPhoneSales"
            placeholder="请选择新电销（可选）"
            filterable
            clearable
            style="width: 100%"
          >
            <el-option
              v-for="sale in salesOptions"
              :key="sale.id"
              :label="sale.name"
              :value="sale.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="新面销">
          <el-select
            v-model="batchForm.newFaceSales"
            placeholder="请选择新面销（可选）"
            filterable
            clearable
            style="width: 100%"
          >
            <el-option
              v-for="sale in salesOptions"
              :key="sale.id"
              :label="sale.name"
              :value="sale.id"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="batchDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="batchLoading" @click="submitBatchTransfer">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getTransferList, createTransfer, getSalesList, batchTransfer } from '../../api/userMG';
import Pagination from '../../components/Pagination';

export default {
  name: 'MerchantTransfer',
  components: { Pagination },
  data() {
    return {
      loading: false,
      submitLoading: false,
      listData: [],
      formInline: {
        page: 1,
        pageSize: 20,
        merchantName: '',
        disCompanyName: '',
        // REQ-003 F04: 四字段下拉（字面字段 ID 等值查询）
        phoneSalesId: null,
        faceSalesId: null,
        transferPhoneSalesId: null,
        transferFaceSalesId: null
      },
      pageparm: {
        page: 1,
        pageSize: 20,
        total: 0
      },
      transferDialogVisible: false,
      currentRow: {},
      transferForm: {
        newPhoneSales: null,
        newFaceSales: null
      },
      transferRules: {},
      // BUG-001: 搜索区四下拉数据源(含在职+离职, 不传 status); 转交/批量转交弹窗目标用 salesOptions(仅在职)
      searchSalesOptions: [],
      salesOptions: [],
      // REQ-003 F05: 批量转交
      selectedRows: [],
      batchDialogVisible: false,
      batchLoading: false,
      crossPage: false,
      batchForm: {
        newPhoneSales: null,
        newFaceSales: null
      }
    };
  },
  created() {
    this.getList();
    this.loadSearchSalesOptions();
    this.loadTransferTargetOptions();
  },
  methods: {
    /** 获取转移列表（GET /v1/sales/transfers，REQ-003 F04 携带四字段 ID 查询） */
    getList() {
      this.loading = true;
      const params = {
        page: this.formInline.page,
        pageSize: this.formInline.pageSize,
        merchantName: this.formInline.merchantName || undefined,
        disCompanyName: this.formInline.disCompanyName || undefined,
        phoneSalesId: this.formInline.phoneSalesId || undefined,
        faceSalesId: this.formInline.faceSalesId || undefined,
        transferPhoneSalesId: this.formInline.transferPhoneSalesId || undefined,
        transferFaceSalesId: this.formInline.transferFaceSalesId || undefined
      };
      getTransferList(params)
        .then((res) => {
          if (res && (res.msg === 'success' || res.code === 0) && res.data) {
            const d = res.data;
            this.listData = Array.isArray(d) ? d : (d.records || d.items || d.list || []);
            this.pageparm.total = d.total != null ? d.total : (d.totalCount || 0);
            this.pageparm.page = d.current != null ? d.current : (d.page || this.formInline.page);
            this.pageparm.pageSize = d.size != null ? d.size : (d.pageSize || this.formInline.pageSize);
          } else {
            this.listData = [];
            const msg = (res && (res.msg || res.message)) || '';
            if (msg && res.msg !== 'success') this.$message.error(msg);
          }
        })
        .catch((err) => {
          this.listData = [];
          // HTTP 4xx/5xx 已由响应拦截器统一提示，此处仅兜底网络异常
          if (!(err && err.response)) {
            const msg = (err && err.message) || '请求失败';
            this.$message.error(msg);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    /** BUG-001: 加载搜索下拉(含在职+离职, GET /v1/sales 不传 status → 后端默认 status!=9), 用于搜索区四字段下拉 */
    loadSearchSalesOptions() {
      getSalesList({ page: 1, pageSize: 1000 })
        .then((res) => {
          if (res && (res.msg === 'success' || res.code === 0) && res.data) {
            const d = res.data;
            const records = Array.isArray(d) ? d : (d.records || d.items || d.list || []);
            this.searchSalesOptions = records.filter((item) => item && item.id != null).map((item) => ({
              id: item.id,
              name: item.name || item.username || '未命名'
            }));
          } else {
            this.searchSalesOptions = [];
            const msg = (res && (res.msg || res.message)) || '';
            if (msg && res.msg !== 'success') this.$message.error(msg);
          }
        })
        .catch((err) => {
          this.searchSalesOptions = [];
          if (!(err && err.response)) {
            const msg = (err && err.message) || '请求失败';
            this.$message.error(msg);
          }
        });
    },
    /** 加载转交目标下拉(仅在职, GET /v1/sales?status=1) — 契约要求转交目标须在职, 离职销售不可作为转交目标 */
    loadTransferTargetOptions() {
      getSalesList({ page: 1, pageSize: 1000, status: 1 })
        .then((res) => {
          if (res && (res.msg === 'success' || res.code === 0) && res.data) {
            const d = res.data;
            const records = Array.isArray(d) ? d : (d.records || d.items || d.list || []);
            this.salesOptions = records.filter((item) => item && item.id != null).map((item) => ({
              id: item.id,
              name: item.name || item.username || '未命名'
            }));
          } else {
            this.salesOptions = [];
            const msg = (res && (res.msg || res.message)) || '';
            if (msg && res.msg !== 'success') this.$message.error(msg);
          }
        })
        .catch((err) => {
          this.salesOptions = [];
          if (!(err && err.response)) {
            const msg = (err && err.message) || '请求失败';
            this.$message.error(msg);
          }
        });
    },
    search() {
      this.formInline.page = 1;
      this.pageparm.page = 1;
      this.getList();
    },
    reset() {
      this.formInline.merchantName = '';
      this.formInline.disCompanyName = '';
      // REQ-003 F04: 重置时一并清空四字段下拉
      this.formInline.phoneSalesId = null;
      this.formInline.faceSalesId = null;
      this.formInline.transferPhoneSalesId = null;
      this.formInline.transferFaceSalesId = null;
      this.formInline.page = 1;
      this.pageparm.page = 1;
      // 清空勾选（含表头全选状态）
      this.clearSelection();
      this.getList();
    },
    /** 行勾选变化（REQ-003 F05: 勾选 ≥1 行后「批量转交」可点击） */
    handleSelectionChange(rows) {
      this.selectedRows = rows || [];
    },
    /** 清空表格勾选 */
    clearSelection() {
      if (this.$refs.tableRef) {
        this.$refs.tableRef.clearSelection();
      }
      this.selectedRows = [];
    },
    /** 打开批量转交弹窗（跨页开关默认关闭，默认只转当前页勾选） */
    openBatchDialog() {
      this.batchForm = {
        newPhoneSales: null,
        newFaceSales: null
      };
      this.crossPage = false;
      this.batchDialogVisible = true;
    },
    resetBatchForm() {
      this.batchForm = {
        newPhoneSales: null,
        newFaceSales: null
      };
      this.crossPage = false;
    },
    /** 组装跨页全选（模式B）搜索条件：与列表查询条件一致，空值剔除 */
    buildSearchParams() {
      const p = {};
      if (this.formInline.merchantName) p.merchantName = this.formInline.merchantName;
      if (this.formInline.disCompanyName) p.disCompanyName = this.formInline.disCompanyName;
      if (this.formInline.phoneSalesId) p.phoneSalesId = this.formInline.phoneSalesId;
      if (this.formInline.faceSalesId) p.faceSalesId = this.formInline.faceSalesId;
      if (this.formInline.transferPhoneSalesId) p.transferPhoneSalesId = this.formInline.transferPhoneSalesId;
      if (this.formInline.transferFaceSalesId) p.transferFaceSalesId = this.formInline.transferFaceSalesId;
      return p;
    },
    /** 提交批量转交（POST /v1/sales/transfers/batch，模式A records / 模式B searchParams） */
    submitBatchTransfer() {
      // 至少转一个（电销/面销都为空 → 拒绝，对应契约规则 + AC-003-22）
      if ((this.batchForm.newPhoneSales == null || this.batchForm.newPhoneSales === '') &&
          (this.batchForm.newFaceSales == null || this.batchForm.newFaceSales === '')) {
        this.$message.warning('请至少选择转交电销或转交面销');
        return;
      }
      const params = {
        newPhoneSales: this.batchForm.newPhoneSales || null,
        newFaceSales: this.batchForm.newFaceSales || null
      };
      if (this.crossPage) {
        // 模式B: 跨页全选 → 携带当前搜索条件（防误全量：搜索条件全空时拒绝）
        const searchParams = this.buildSearchParams();
        if (Object.keys(searchParams).length === 0) {
          this.$message.warning('当前无搜索条件，无法全选当前搜索结果转交，请先设置查询条件');
          return;
        }
        params.searchParams = searchParams;
      } else {
        // 模式A: 勾选记录列表
        if (this.selectedRows.length === 0) {
          this.$message.warning('请先勾选要转交的记录');
          return;
        }
        params.records = this.selectedRows.map((row) => ({
          merchant: row.merchant,
          disCompany: row.disCompany
        }));
      }
      this.batchLoading = true;
      batchTransfer(params)
        .then((res) => {
          if (res && (res.msg === 'success' || res.code === 0)) {
            const transferred = (res.data && res.data.transferred) || 0;
            this.$message.success('批量转交成功，共 ' + transferred + ' 条');
            this.batchDialogVisible = false;
            this.clearSelection();
            this.getList();
          } else {
            const msg = (res && (res.msg || res.message)) || '批量转交失败';
            this.$message.error(msg);
          }
        })
        .catch((err) => {
          if (!(err && err.response)) {
            const msg = (err && err.message) || '批量转交失败';
            this.$message.error(msg);
          }
        })
        .finally(() => {
          this.batchLoading = false;
        });
    },
    callFather(parm) {
      this.formInline.page = parm.page;
      this.formInline.pageSize = parm.pageSize;
      this.pageparm.page = parm.page;
      this.pageparm.pageSize = parm.pageSize;
      this.getList();
    },
    /** 打开转交弹窗 */
    openTransferDialog(row) {
      this.currentRow = row || {};
      this.transferForm = {
        newPhoneSales: null,
        newFaceSales: null
      };
      this.transferDialogVisible = true;
      this.$nextTick(() => {
        this.$refs.transferFormRef && this.$refs.transferFormRef.clearValidate();
      });
    },
    resetTransferForm() {
      this.currentRow = {};
      this.transferForm = {
        newPhoneSales: null,
        newFaceSales: null
      };
      this.$refs.transferFormRef && this.$refs.transferFormRef.clearValidate();
    },
    /** 提交转交（POST /v1/sales/transfers） */
    submitTransfer() {
      // 至少转一个（电销/面销都为空 → 拒绝，对应契约规则 + AC-002-07）
      if ((this.transferForm.newPhoneSales == null || this.transferForm.newPhoneSales === '') &&
          (this.transferForm.newFaceSales == null || this.transferForm.newFaceSales === '')) {
        this.$message.warning('请至少选择转交电销或转交面销');
        return;
      }
      this.submitLoading = true;
      const row = this.currentRow;
      const params = {
        merchant: row.merchant,
        disCompany: row.disCompany,
        newPhoneSales: this.transferForm.newPhoneSales || null,
        newFaceSales: this.transferForm.newFaceSales || null
      };
      createTransfer(params)
        .then((res) => {
          if (res && (res.msg === 'success' || res.code === 0)) {
            this.$message.success('转交成功');
            this.transferDialogVisible = false;
            this.getList();
          } else {
            const msg = (res && (res.msg || res.message)) || '转交失败';
            this.$message.error(msg);
          }
        })
        .catch((err) => {
          if (!(err && err.response)) {
            const msg = (err && err.message) || '转交失败';
            this.$message.error(msg);
          }
        })
        .finally(() => {
          this.submitLoading = false;
        });
    }
  }
};
</script>

<style scoped>
.merchant-transfer-page {
  padding-bottom: 24px;
}
.page-breadcrumb {
  margin-bottom: 0;
}
.search-section {
  margin-top: 24px;
  margin-bottom: 16px;
  padding: 16px 20px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}
.search-form .el-form-item {
  margin-bottom: 10px;
  margin-right: 20px;
}
.search-form .search-input {
  width: 220px;
}
/* REQ-003 F04: 四字段下拉与输入框同宽，换行后保持整齐 */
.search-form .search-select {
  width: 220px;
}
.table-card {
  border-radius: 8px;
  overflow: hidden;
}
.table-card >>> .el-card__body {
  padding: 20px;
}
/* REQ-003 F05: 批量操作区 */
.batch-toolbar {
  margin-bottom: 14px;
  display: flex;
  align-items: center;
}
.batch-selected-tip {
  margin-left: 12px;
  font-size: 13px;
  color: #606266;
}
.pagination-wrap {
  margin-top: 16px;
}
.transfer-dialog >>> .el-dialog {
  border-radius: 8px;
}
.transfer-dialog >>> .el-dialog__header {
  padding: 16px 20px;
  border-bottom: 1px solid #ebeef5;
}
.transfer-dialog >>> .el-dialog__title {
  font-size: 16px;
  color: #303133;
}
.transfer-dialog >>> .el-dialog__body {
  padding: 16px 20px 10px;
}
.transfer-dialog >>> .el-dialog__body .el-form-item {
  margin-bottom: 12px;
}
.transfer-dialog >>> .el-dialog__footer {
  padding: 12px 20px 20px;
  text-align: right;
}
/* REQ-003 F03: 提示区与表单 label 左边缘对齐（去左右 padding），下拉右边缘对齐（width:100%） */
.transfer-tip {
  margin-bottom: 16px;
  padding: 12px 0;
  background: #f5f7fa;
  border-radius: 6px;
  color: #606266;
  font-size: 14px;
  line-height: 24px;
}
.transfer-tip .tip-strong {
  color: #303133;
  font-weight: 600;
}
.transfer-tip .tip-small {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}
/* REQ-003 F05: 批量弹窗跨页开关与表单间距 */
.batch-cross-checkbox {
  margin: 0 0 14px;
}
.batch-form >>> .el-form-item {
  margin-bottom: 12px;
}
</style>



// WEBPACK FOOTER //
// src/views/oms/merchantTransfer.vue